import { useRef, useState } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { ConfirmModal } from '../common/Modal';
import {
  ProjectQueueConfigEditor,
  RepositoryProvisioningList,
  hasPerPersonBuckets,
} from './ProjectQueueConfigEditor';
import { useProjects } from '../../hooks/useProjects';
import { useProjectStates } from '../../hooks/useProjectStates';
import {
  useProjectQueueConfigs,
  useUpsertProjectQueueConfig,
  useDisableProjectQueueConfig,
  useRetryRepositoryProvisioning,
  usePaperclipCatalog,
  useRefreshPaperclipCatalog,
} from '../../hooks/useBotAdmin';
import {
  useProjectRepositories,
  useAvailableRepositories,
  useLinkRepository,
} from '../../hooks/useProjectRepositories';
import type {
  ProjectQueueConfig,
  RepositoryProvisioningEntry,
} from '../../services/api/botAdmin';

/**
 * MKS-471 (Plan 128-07 / D-14 trigger 4, D-21): Bot Admin's provisioning list. Owns the retry
 * mutation, so the hook is only ever mounted for a project that has something to retry (the editor
 * renders this only when the stored row has provisioning entries).
 */
function AdminRepositoryProvisioning({
  projectId,
  entries,
}: {
  projectId: number;
  entries: RepositoryProvisioningEntry[];
}) {
  const retryMutation = useRetryRepositoryProvisioning();
  return (
    <RepositoryProvisioningList
      entries={entries}
      onRetry={(repository) => retryMutation.mutate({ projectId, repository })}
      retryingRepository={retryMutation.isPending ? (retryMutation.variables?.repository ?? '') : null}
      retryFailed={retryMutation.isError}
    />
  );
}

/**
 * Phase 80 (Plan 80-06 / BOTQ-09) — per-project bot work-queue config admin
 * surface. An admin picks a project, toggles the queue on, chooses which
 * source columns feed it, and sets cadence/budget.
 *
 * Structural sibling of `ChannelProjectLinks.tsx`: same card shell,
 * server-confirm-only mutations (T-80-06-STALE — no optimistic update, so a
 * failed save never shows as applied), `ConfirmModal` for the destructive
 * disable action.
 *
 * Phase 129 (Plan 129-06 / D-11): the field logic lives in `ProjectQueueConfigEditor`, shared with
 * the project Engineering Robot card. This wrapper owns what is Bot Admin's alone: the project
 * picker, the inline GitHub link, the catalog Refresh, the all-projects table and the Disable modal.
 */
export function ProjectQueueConfigForm() {
  const { data: configs, isLoading, error } = useProjectQueueConfigs();
  const { data: projects } = useProjects();
  const upsertMutation = useUpsertProjectQueueConfig();
  const disableMutation = useDisableProjectQueueConfig();
  // Phase 115 (Plan 115-04 / PCPROV-01) — the stored, dated Factory™
  // catalog backing the two pickers in the editor. Never blocks: `catalog` is
  // undefined only while the query itself is loading, in which case both
  // fields fall back to the free-text inputs exactly as an empty catalog
  // would (D-115-03's "never lock the admin out" contract).
  const { data: catalog } = usePaperclipCatalog();
  const refreshCatalogMutation = useRefreshPaperclipCatalog();

  const [projectId, setProjectId] = useState('');
  // Bumped on every load (select or pencil) so the editor re-seeds from the stored row, which is
  // what the old loadExistingConfig did, even when the same project is picked again.
  const [editorNonce, setEditorNonce] = useState(0);
  // Bumped after a disable of the selected project, so the editor shows Enabled off.
  const [disabledSignal, setDisabledSignal] = useState(0);
  const [pendingDisable, setPendingDisable] = useState<ProjectQueueConfig | null>(null);
  const [repoToLink, setRepoToLink] = useState('');

  // The editor, so the row pencil can bring it into view (see openFullEdit).
  const editorRef = useRef<HTMLDivElement>(null);

  const rows = configs ?? [];
  // WR-04 (80-REVIEW): the prefill reads from `rows` — selecting a project
  // before the configs query settles would seed defaults and a Save would
  // full-replace (PUT) the project's real config. Block selection until then.
  const configsReady = !isLoading && !error;
  // Only active (in_progress) projects, sorted by name — a queue config
  // targets a live project, same convention as ChannelProjectLinks.
  const projectOptions = (projects ?? [])
    .filter((p) => p.state === 'in_progress')
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedProjectId = projectId ? Number(projectId) : undefined;
  const { data: projectStates } = useProjectStates(selectedProjectId);
  // GitHub repo linkage — bot coding can't run without a connected repo, so
  // surface the selected project's repo here and let an admin link one inline
  // when none is connected. Reuses the Phase 32 project↔repository data layer.
  const { data: linkedRepos, isLoading: reposLoading } = useProjectRepositories(selectedProjectId);
  const hasRepo = (linkedRepos?.length ?? 0) > 0;
  const { data: availableRepos } = useAvailableRepositories(!!selectedProjectId && !hasRepo);
  const linkRepoMutation = useLinkRepository(selectedProjectId ?? 0);

  // Phase 118: the stored row for the selected project backs the editor's read-only
  // "used today" and "Current WIP" displays.
  const selectedRow = selectedProjectId
    ? rows.find((c) => c.projectId === selectedProjectId)
    : undefined;

  const loadExistingConfig = (id: string) => {
    setProjectId(id);
    setEditorNonce((n) => n + 1);
    setRepoToLink('');
  };

  const handleLinkRepo = () => {
    if (!selectedProjectId || !repoToLink) return;
    linkRepoMutation.mutate(repoToLink, { onSuccess: () => setRepoToLink('') });
  };

  // The row pencil opens the FULL form above for that project. It used to open an inline row
  // editor that had no SRE switch, mode or budgets, so those were reachable only through the
  // Project dropdown, which nobody found.
  const openFullEdit = (row: ProjectQueueConfig) => {
    loadExistingConfig(String(row.projectId));
    editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const closeDisableModal = () => {
    if (disableMutation.isPending) return;
    setPendingDisable(null);
  };

  const confirmDisable = () => {
    if (!pendingDisable) return;
    disableMutation.mutate(pendingDisable.projectId, {
      onSuccess: () => {
        setPendingDisable(null);
        if (selectedProjectId === pendingDisable.projectId) {
          setDisabledSignal((n) => n + 1);
        }
      },
    });
  };

  const githubLinkSlot = (
    <div>
      <p className="mb-1 text-sm font-medium text-gray-700">GitHub repository</p>
      {reposLoading ? (
        <p className="text-sm text-gray-400">Loading repository…</p>
      ) : hasRepo ? (
        <div className="flex flex-wrap gap-2">
          {linkedRepos!.map((pr) => (
            <span
              key={pr.repository.id}
              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
            >
              {pr.repository.owner}/{pr.repository.name}
            </span>
          ))}
        </div>
      ) : (
        <div className="space-y-1.5">
          <p className="text-sm text-amber-600">
            No repository connected — link one, or bot coding cannot run for this project.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="GitHub repository"
              value={repoToLink}
              onChange={(e) => setRepoToLink(e.target.value)}
              className="w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select a repository…</option>
              {(availableRepos ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.owner}/{r.name}
                </option>
              ))}
            </select>
            <Button
              type="button"
              onClick={handleLinkRepo}
              disabled={!repoToLink}
              isLoading={linkRepoMutation.isPending}
            >
              Link
            </Button>
          </div>
          {linkRepoMutation.isError && (
            <p className="text-sm text-red-600">{(linkRepoMutation.error as Error).message}</p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-sm font-semibold text-gray-900">Project bot work-queue config</p>
      <p className="text-sm text-gray-500 mt-0.5">
        Turn the bot work queue on for a project, pick which columns feed it, and set cadence and
        daily budget.
      </p>

      <div className="mt-3 space-y-3">
        <div>
          <label
            htmlFor="queue-config-project"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Project
          </label>
          <select
            id="queue-config-project"
            value={projectId}
            onChange={(e) => loadExistingConfig(e.target.value)}
            disabled={!configsReady}
            className="w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">Select a project…</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.slug})
              </option>
            ))}
          </select>
        </div>

        {selectedProjectId && (
          <div ref={editorRef}>
            <ProjectQueueConfigEditor
              key={`${selectedProjectId}:${editorNonce}`}
              mode="admin"
              projectId={selectedProjectId}
              row={selectedRow}
              projectStates={projectStates}
              catalog={catalog}
              onSave={(input, options) => {
                if (!configsReady) return;
                upsertMutation.mutate(
                  { projectId: selectedProjectId, input: { autoMergeOnGreen: false, ...input } },
                  options
                );
              }}
              savePending={upsertMutation.isPending}
              saveError={upsertMutation.isError ? (upsertMutation.error as Error).message : null}
              renderProvisioning={(entries) => (
                <AdminRepositoryProvisioning projectId={selectedProjectId} entries={entries} />
              )}
              githubLinkSlot={githubLinkSlot}
              catalogRefreshSlot={({ paperclipCompanyId }) => (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      // The company is the request's signing scope, not a filter — the plugin
                      // can only authenticate a request that names a company. Blank lets the
                      // backend fall back to an already-bound project's company.
                      refreshCatalogMutation.mutate({
                        projectId: selectedProjectId,
                        companyId: paperclipCompanyId.trim() || undefined,
                      })
                    }
                    disabled={refreshCatalogMutation.isPending}
                    className="font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:hover:text-blue-600"
                  >
                    {refreshCatalogMutation.isPending ? 'Requesting…' : 'Refresh'}
                  </button>
                  {refreshCatalogMutation.isSuccess && (
                    <p className="w-full basis-full text-xs text-gray-400">
                      Refresh requested — the answer arrives asynchronously; re-check shortly
                      rather than waiting here.
                    </p>
                  )}
                  {refreshCatalogMutation.isError && (
                    <p className="w-full basis-full text-xs text-red-600">
                      {(refreshCatalogMutation.error as Error).message}
                    </p>
                  )}
                </>
              )}
              disabledSignal={disabledSignal}
            />
          </div>
        )}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm text-gray-400">Loading queue configs…</p>
        ) : error ? (
          <p className="text-sm text-red-600">Failed to load queue configs.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">No project queue configs yet.</p>
        ) : (
          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Project
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Cadence
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Budget used
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Triager
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Source columns
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row, idx) => {
                  return (
                    <tr key={row.id} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        {row.projectName ?? `#${row.projectId}`}
                        {row.projectSlug ? (
                          <span className="text-xs text-gray-400"> ({row.projectSlug})</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-sm whitespace-nowrap">
                        {row.enabled ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {row.pollIntervalSeconds}s
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                        {row.dailyBudgetUsedCount} /{' '}
                        {row.dailyBudget}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                          <>
                            {row.triagerMode === 'propose'
                              ? 'Propose'
                              : row.triagerMode === 'auto'
                                ? 'Auto'
                                : 'Off'}
                            <span className="text-xs text-gray-500">
                              {' '}
                              <span
                                title={
                                  hasPerPersonBuckets(row.triagerWip.buckets)
                                    ? 'Total work in progress; the limit applies per person'
                                    : undefined
                                }
                              >
                                {row.triagerWip.count}/{row.triagerWip.limit}
                              </span>
                            </span>
                          </>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {row.sourceTaskStateIds.length === 0 ? (
                          <span className="text-gray-400">None</span>
                        ) : (
                          <>
                            {row.sourceTaskStates.map((s) => s.name).join(', ')}
                            {row.sourceTaskStateIds
                              .filter((id) => !row.sourceTaskStates.some((s) => s.id === id))
                              .map((id) => (
                                <span key={id} className="text-xs text-gray-400">
                                  {row.sourceTaskStates.length > 0 ? ' ' : ''}#{id} (removed)
                                </span>
                              ))}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openFullEdit(row)}
                              aria-label={`Edit queue config for ${row.projectName ?? row.projectId}`}
                              className="text-gray-400 hover:text-blue-600"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDisable(row)}
                              disabled={!row.enabled}
                              aria-label={`Disable queue config for ${row.projectName ?? row.projectId}`}
                              className="text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:hover:text-gray-400"
                            >
                              Disable
                            </button>
                          </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pendingDisable && (
        <ConfirmModal
          isOpen={true}
          onClose={closeDisableModal}
          onConfirm={confirmDisable}
          title={`Disable queue for ${pendingDisable.projectName ?? `#${pendingDisable.projectId}`}?`}
          message="The bot will stop pulling tasks from this project's queue. You can re-enable it any time."
          confirmText="Disable"
          cancelText="Cancel"
          variant="danger"
          isLoading={disableMutation.isPending}
          error={disableMutation.isError ? (disableMutation.error as Error).message : null}
        />
      )}
    </div>
  );
}
